import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, type JSX } from 'react';
import { useAuthStore } from './state/auth.store';
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { JoinPage } from './features/auth/JoinPage';
import { LobbyPage } from './features/lobby/LobbyPage';
import { RoomGate } from './features/office/RoomGate';
import { EditorPage } from './features/editor/EditorPage';
import { isEditorEnabled } from './features/editor/editor-flag';

// The marketing landing ("/", ADR-0042) is code-split: it (and its Pixi hero) never load for players who
// go straight into a room, and a returning authed device redirects out of it before any of it paints.
const LandingPage = lazy(() =>
  import('./features/landing/LandingPage').then((m) => ({ default: m.LandingPage })),
);

/**
 * Strict login wall (ТЗ §1): any route requiring an account sends an unauthenticated visitor to /login
 * and RETURNS them to the page they wanted (via `location.state.from`) once they sign in. There is no
 * guest-first entry — every room, the lobby, and the editor require a registered account. A returning
 * device with a valid/refreshable token passes straight through.
 */
function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const authed = useAuthStore((s) => s.tokens !== null);
  const location = useLocation();
  return authed ? children : <Navigate to="/login" replace state={{ from: location }} />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      {/* New-visitor front door (ADR-0042): the premium marketing landing. Code-split + Suspense so it
          never weighs on a player entering a room. An authed visitor still sees it (no silent redirect),
          with its CTAs pointing at the lobby — the hub — rather than straight into a room. */}
      <Route
        path="/"
        element={
          <Suspense fallback={null}>
            <LandingPage />
          </Suspense>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* The LOBBY is the hub (ADR-0055): the main way into any room. Behind the login wall (ТЗ §1) —
          you must be a registered account to browse or enter. A returning session with no specific room
          link lands here; a direct room link (below) still walks straight into that room. */}
      <Route
        path="/lobby"
        element={
          <RequireAuth>
            <LobbyPage />
          </RequireAuth>
        }
      />
      <Route path="/join/:key" element={<JoinPage />} />
      {/* Level editor — a dev/admin creator tool, OFF for players by default (see editor-flag). It needs
          no room/auth: it authors level DATA locally. A disabled flag falls through to the lobby. */}
      <Route path="/editor" element={isEditorEnabled() ? <EditorPage /> : <Navigate to="/lobby" replace />} />
      {/* The room deep-link (ADR-0055): the URL is the SOURCE OF TRUTH for which room you're in. RoomGate
          resolves `/{level_type}/{room_key}` (slug or id) BEFORE connecting and guarantees the token is
          scoped to THAT room, then mounts OfficePage (which canonicalizes the address bar from there).
          The bare legacy `/office` no longer enters a room — rooms are reached only via the lobby or a
          direct room link — so it redirects to the hub. React Router v6 ranks the STATIC routes above the
          dynamic two-segment pattern, so /login, /register, /lobby, /join/:key, /editor always win. */}
      <Route path="/office" element={<Navigate to="/lobby" replace />} />
      <Route path="/:levelType/:roomKey" element={<RoomGate />} />
      {/* Any unknown path carries neither an invite nor a room id, so it's a non-specific arrival → the
          marketing landing `/` (the front door), shown to everyone. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
